import React, { useState, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  getCountryAll,
  deleteCountry,
  createCountry,
  updateCountry,
  softDeleteCountry,
} from "../../Services/CountryService";
import { getRegionAll } from "../../Services/RegionService";
import Swal from "sweetalert2";
import MTable from "../../Components/MTable/MTable";
import { InputSwitch } from "primereact/inputswitch";
import Overlay from "../../Components/Overlay/Overlay";
import { toastError, toastSuccess } from "../../Components/Toasts/Toast";
import { handlerFormError, uuid } from "../../Utils/Utils";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import EditButton from "../../Components/Button/EditButton";
import DeleteButton from "../../Components/Button/DeleteButton";

let processingId = -1;

export default function CountryPage() {
  let { userInfo } = useSelector((state) => state.auth);
  const [showAll, setShowAll] = useState(false);
  const mTable = useRef();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      id_region: "",
      country_code: "",
      country_name: "",
      is_active: true,
    },
  });
  const [state, setState] = useState({
    showForm: false,
    country: {
      id_region: "",
      country_code: "",
      country_name: "",
      is_active: true,
    },
    regions: [],
    processing: false,
  });

  const startProcessing = () => {
    processingId = setTimeout(() => {
      setState({ ...state, processing: true });
    }, 150);
  };

  const stopProcessing = () => {
    clearTimeout(processingId);
    setState({ ...state, processing: false });
  };

  const removeData = (id) => {
    deleteCountry(
      id,
      (res) => {
        if (res.status == 200 || res.status == 201) {
          Swal.fire({
            icon: "success",
            title: "Delete data success",
            text: "Data has been deleted!",
          }).then((res) => {
            mTable.current.refresh();
          });
        }
      },
      ({ response: { data } }) => {
        Swal.fire({
          icon: "error",
          title: "Delete data fail",
          text: data.message,
        });
      }
    );
  };

  const onEdit = (item) => () => {
    if (!userInfo.access.master_country.can_update) {
      return;
    }
    reset(item);
    setState({ ...state, country: item, showForm: true });
  };

  const onRemove = (item) => () => {
    if (!userInfo.access.master_country.can_delete) {
      toastError("You dont have permission for this action");
      return;
    }

    Swal.fire({
      icon: "question",
      title: "Are you sure?",
      text: "Deleted data can not be restored!",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
    }).then(({ isConfirmed }) => {
      if (isConfirmed) {
        removeData(item.id);
      }
    });
  };

  const onSubmit = (data) => {
    startProcessing();
    const response = country.id
      ? updateCountry(country.id, data)
      : createCountry(data);
    response
      .then((res) => {
        if (res.status == 200 || res.status == 201) {
          Swal.fire({
            icon: "success",
            title: "Save data success",
            text: "Data has been saved!",
          }).then((r) => {
            mTable.current.refresh();
            country.id ? onCloseForm() : onReset();
          });
        }
      })
      .catch(({ response: { data } }) => {
        handlerFormError(data);
      })
      .finally((_) => stopProcessing());
  };

  const onReset = () => {
    reset({
      id_region: "",
      country_code: "",
      country_name: "",
      is_active: true,
    });
    setState({
      ...state,
      country: {},
    });
  };

  const onCloseForm = () => {
    reset({
      id_region: "",
      country_code: "",
      country_name: "",
      is_active: true,
    });
    setState({ ...state, country: {}, showForm: false });
  };

  const { country, processing, showForm } = state;

  const onActiveChange = (item) => (e) => {
    if (!userInfo.access.master_country.can_update) {
      toastError("You dont have permission for this action");
      return;
    }

    const status = e.target.value ? "Activate" : "Inactive";
    softDeleteCountry(item.id, { is_active: e.target.value })
      .then((res) => {
        toastSuccess(`${status} ${item.country_name} success`);
        mTable.current.refresh();
      })
      .catch(({ response: { err } }) => {
        toastError(`${status} ${item.name} Failed.`);
      });
  };

  const onAddData = () => {
    if (!userInfo.access.master_country.can_create) {
      return;
    }
    onReset();
    setState({ ...state, showForm: true });
  };

  useEffect(() => {
    getRegionAll({ perpage: 100 }, ({ data }) => {
      setState({ ...state, regions: data.data });
    });
  }, []);

  const columns = [
    {
      id: uuid(),
      title: "",
      field: "",
      render: (item) => {
        return (
          <img
            src={`https://flagcdn.com/20x15/${item.country_code}.png`}
            width={25}
            height={15}
          />
        );
      },
    },
    {
      id: uuid(),
      title: "Country Name",
      field: "country_name",
      sortable: true,
    },
    {
      id: 2,
      title: "Country Code",
      field: "country_code",
      sortable: true,
      render: (item) => {
        return item.country_code;
      },
    },
    {
      id: uuid(),
      title: "Active",
      field: "is_active",
      render: (item) => {
        if (item.is_active === true) {
          return <span className="badge badge-success">Active</span>;
        } else {
          return <span className="badge badge-secondary">Inactive</span>;
        }
      },
    },
    {
      id: uuid(),
      title: "",
      sortable: true,
      style: { width: 200 },
      render: (item) => {
        return (
          <div>
            {userInfo.access.master_country.can_update && (
              <EditButton title="Edit" onClick={onEdit(item)} />
            )}
            {userInfo.access.master_country.can_delete && (
              <DeleteButton title="Remove" onClick={onRemove(item)} />
            )}
          </div>
        );
      },
    },
  ];

  const showAllFunc = () => {
    setShowAll(!showAll);
    mTable.current.refresh();
  };

  const propsTable = {
    columns,
    showAllState: showAll,
    showAllFunc,
    getData: (params) => {
      if (showAll === false) {
        if (params.filterAnd) params.filterAnd += "," + `is_active:true`;
        else params.filterAnd = "is_active:true";
      }
      return getCountryAll({ ...params });
    },
    showIndex: true,
    enableFilter: true,
    showAddButton: !userInfo.access.master_country.can_create ? false : true,
  };

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h4
                style={{
                  marginTop: "0.5rem",
                  marginLeft: "0.2rem",
                  fontWeight: "400",
                }}
              >
                {showForm ? "Country CForm" : "Country Management"}
              </h4>
              <h6
                style={{
                  marginLeft: "0.2rem",
                  fontWeight: "200",
                  letterSpacing: "1.2px",
                }}
              >
                {showForm ? "Add/edit a country" : "Database of Countries"}
              </h6>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <NavLink to="/home">Home</NavLink>
                </li>
                <li className="breadcrumb-item active">Country</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <Overlay display={processing} />

                <form name="form-detail" onSubmit={handleSubmit(onSubmit)}>
                  <div className="card-body">
                    <div
                      className={`${
                        !showForm
                          ? "card__side--front-show"
                          : "card__side--front"
                      }`}
                    >
                      <MTable
                        ref={mTable}
                        {...propsTable}
                        onAddData={onAddData}
                        showIndex={true}
                      />
                    </div>

                    <div
                      className={`${
                        showForm ? "card__side--back-show" : "card__side--back"
                      }`}
                    >
                      <div className="row">
                        <div className="col-md-4">
                          <div className="form-group">
                            <label>Region</label>
                            <select
                              id="id_region"
                              className="form-control"
                              {...register("id_region", {
                                required: {
                                  value: true,
                                  message: "Region is required!",
                                },
                              })}
                            >
                              <option value={""}>Select Region</option>
                              {state.regions.map((seg, i) => (
                                <option key={`region-` + i} value={seg.id}>
                                  {seg.region_name}
                                </option>
                              ))}
                            </select>
                            {errors.id_region && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.id_region.message}
                              </span>
                            )}
                          </div>

                          <div className="form-group">
                            <label htmlFor="country_code">Country Code</label>
                            <input
                              id="country_code"
                              {...register("country_code", {
                                required: {
                                  value: true,
                                  message: "Country code is required!",
                                },
                              })}
                              placeholder="Country code"
                              className="form-control"
                              disabled={country.id ? true : false}
                            />
                            {errors.country_code && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.country_code.message}
                              </span>
                            )}
                          </div>
                          <div className="form-group">
                            <label htmlFor="country_name">Country Name</label>
                            <input
                              id="country_name"
                              {...register("country_name", {
                                required: {
                                  value: true,
                                  message: "Country name is required!",
                                },
                              })}
                              placeholder="Country name"
                              className="form-control"
                            />
                            {errors.country_name && (
                              <span
                                className="text-danger"
                                style={{ fontSize: 14 }}
                              >
                                {errors.country_name.message}
                              </span>
                            )}
                          </div>
                          <div className="form-group">
                            <label htmlFor="is_active">Active</label>
                            <div>
                              <Controller
                                name="is_active"
                                control={control}
                                render={({ field }) => {
                                  return (
                                    <InputSwitch
                                      {...field}
                                      checked={field.value}
                                    />
                                  );
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {showForm && (
                    <div className="card-footer delay">
                      <div className="row">
                        <div className="col-md-12">
                          <div
                            className="d-flex"
                            style={{ justifyContent: "flex-end" }}
                          >
                            <button
                              type="button"
                              className="btn btn-default mr-3"
                              style={{ width: 100 }}
                              onClick={onCloseForm}
                            >
                              <i className="fa fa-times" /> Close
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger mr-3"
                              style={{ width: 100 }}
                              onClick={onReset}
                            >
                              <i className="fa fa-sync" /> Clear
                            </button>
                            <button
                              type="submit"
                              className="btn btn-dark"
                              style={{ width: 100 }}
                              disabled={
                                control._formValues.id
                                  ? !userInfo.access.master_country.can_update
                                  : !userInfo.access.master_country.can_create
                              }
                            >
                              <i className="fa fa-save" /> Save
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
